import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import FinancialDocumentRowFormModal from 'src/features/financialDocumentRows/components/forms/FinancialDocumentRowFormModal';
import { useUpdateFinancialDocumentRow } from 'src/features/financialDocumentRows/api/useUpdateFinancialDocumentRow';
import { LIST_FINANCIAL_DOCUMENT_ROWS_RESOURCE } from 'src/features/financialDocumentRows/api/useListFinancialDocumentRows';
import { FinancialDocumentRow } from 'src/features/financialDocumentRows/utils/types';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { useParams } from 'react-router-dom';
import { FINANCIAL_DOCUMENT_RESOURCE } from 'src/features/financialDocuments/api/useGetFinancialDocument';

interface UpdateFinancialDocumentRowFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDocumentRow: FinancialDocumentRow;
    children?: React.ReactNode;
}

function UpdateFinancialDocumentRowFormModal<TData>({
    className,
    semantics,
    children,
    selectedDocumentRow,
}: UpdateFinancialDocumentRowFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { financialDocumentId = '' } = useParams();

    const { mutate, isPending } = useUpdateFinancialDocumentRow({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENT_ROWS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            toast({
                title: formatMessage(financialDocumentRowMessages.updateFinancialDocumentRowSuccess),
                description: formatMessage(financialDocumentRowMessages.updateFinancialDocumentRowSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <FinancialDocumentRowFormModal
            selectedRow={selectedDocumentRow}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate(
                    { ...values, financial_document: financialDocumentId, rowId: selectedDocumentRow.id },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(financialDocumentRowMessages.editFinancialDocumentRowSubtitle, {
                entity: selectedDocumentRow.name,
            })}
        >
            {children}
        </FinancialDocumentRowFormModal>
    );
}

export default UpdateFinancialDocumentRowFormModal;
